import { type ReactNode } from 'react'
import {
  isDefined,
  isTeamId,
  isTeamPreference,
  type CompanyId,
  type EntityId,
  type Preference,
  type TeamId,
} from '@terros/common'
import { Link } from '@tanstack/react-router'
import { Anchor, Group, Text } from '@mantine/core'
import {
  getTeamFullName,
  PREFERENCE_DEFINITIONS,
  usePreferenceDetail,
  useProfile,
  useTeam,
  useTranslation,
} from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'
import { formatPreferenceValue } from './formatPreferenceValue'

type Props = {
  targetId: EntityId
  preference: Preference
  current: unknown
}

export const PreferenceOverrideIndicator = ({ targetId, preference, current }: Props): ReactNode => {
  if (!isTeamId(targetId) || !isTeamPreference(preference) || !isDefined(current)) return
  return <InheritedFrom targetId={targetId} preference={preference} />
}

type InheritedFromProps = Pick<Props, 'preference'> & { targetId: TeamId }

const InheritedFrom = ({ targetId, preference }: InheritedFromProps): ReactNode => {
  const { data, isLoading, error } = useTeam(targetId)
  if (isLoading || error || !data) return
  const parentId = data?.team.parentId
  if (parentId) return <ParentSource parentId={parentId} preference={preference} />
  return <CompanySource preference={preference} />
}

type ParentSourceProps = Pick<Props, 'preference'> & { parentId: TeamId }

const ParentSource = ({ parentId, preference }: ParentSourceProps): ReactNode => {
  const preferenceState = usePreferenceDetail(preference, parentId)
  const teamState = useTeam(parentId)
  const { company } = useProfile()
  if (preferenceState.isLoading || teamState.isLoading || preferenceState.error || teamState.error) return
  if (!preferenceState.data || !teamState.data) return
  if (!isDefined(preferenceState.data.current)) return <InheritedFrom targetId={parentId} preference={preference} />
  const source = getTeamFullName(company, teamState.data.team)
  return (
    <OverrideNotice preference={preference} value={preferenceState.data.current} source={source} teamId={parentId} />
  )
}

const CompanySource = ({ preference }: Pick<Props, 'preference'>): ReactNode => {
  const { company, isLoading, error } = useProfile()
  if (isLoading || error || !company) return
  return <LoadedCompanySource preference={preference} companyId={company.companyId} />
}

type LoadedCompanySourceProps = Pick<Props, 'preference'> & { companyId: CompanyId }

const LoadedCompanySource = ({ preference, companyId }: LoadedCompanySourceProps): ReactNode => {
  const preferenceState = usePreferenceDetail(preference, companyId)
  if (preferenceState.isLoading || preferenceState.error || !preferenceState.data) return
  return <OverrideNotice preference={preference} value={preferenceState.data.value} source='company' />
}

type NoticeProps = Pick<Props, 'preference'> & { value: unknown; source: string; teamId?: TeamId }

const OverrideNotice = ({ preference, value, source, teamId }: NoticeProps): ReactNode => {
  const { t } = useTranslation()
  const params = new URLSearchParams({ selectedGroup: PREFERENCE_DEFINITIONS[preference].group })
  if (teamId) params.set('teamId', teamId)
  return (
    <Group gap='xs' wrap='nowrap' c='yellow.8'>
      <MaterialSymbolIcon icon='call_split' size={16} />
      <Text size='xs' inherit>
        {t('Overrides {{source}}: {{value}}', { source: t(source), value: t(formatPreferenceValue(value)) })}{' '}
        <Anchor component={Link} size='xs' to={`/settings/configuration?${params.toString()}`}>
          {t('View setting')}
        </Anchor>
      </Text>
    </Group>
  )
}
